import { hashStringToID } from "./string";
import { getUniqueID } from "./numbers";
import { IMAGE_FORMATS, VIDEO_FORMATS } from "./constants";

const CREATION_TIME_PAD_INVERT = 9_999_999_999_999;

const normalizeMetaAdTextItems = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [{ id: getUniqueID(), value: "" }];
  }

  return items.map((item) => {
    if (item && typeof item === "object") {
      return {
        id: item.id || getUniqueID(),
        value: item.value ?? "",
      };
    }
    return { id: getUniqueID(), value: item || "" };
  });
};

/**
 * Writes a reusable Meta creative library snapshot into the ad form path.
 * Returns the list of field paths that were set (useful for debugging).
 */
export const applyMetaReusableCreativeToAd = ({
  setValue,
  basicPath,
  creative = {},
} = {}) => {
  if (!setValue || !basicPath || !creative?.creativeID) {
    return [];
  }

  const mediaUrls = Array.isArray(creative.mediaUrls)
    ? creative.mediaUrls.filter(Boolean)
    : creative.url
      ? [creative.url]
      : [];
  const isCarousel = Boolean(
    creative.isCarousel ??
      (mediaUrls.length > 1 || creative.contentType === "carousel")
  );
  const mediaData = Array.isArray(creative.mediaData)
    ? creative.mediaData
    : mediaUrls.map(() => ({ from: "reuseCreative" }));

  const updates = [
    ["reuseExistingCreative", true],
    ["isPostAd", false],
    ["adSetupMode", "reuse"],
    ["creativeID", creative.creativeID || ""],
    ["isPartnershipAd", false],
    ["mediaUrls", mediaUrls],
    ["mediaIDs", Array.isArray(creative.mediaIDs) ? creative.mediaIDs : []],
    ["mediaData", mediaData],
    ["isCarousel", isCarousel],
    ["titles", normalizeMetaAdTextItems(creative.titles)],
    ["headlines", normalizeMetaAdTextItems(creative.headlines)],
    ["descriptions", normalizeMetaAdTextItems(creative.descriptions)],
    ["destinationUrls", normalizeMetaAdTextItems(creative.destinationUrls)],
    ["callToAction", creative.callToAction || ""],
    [
      "callToActions",
      Array.isArray(creative.callToActions) ? creative.callToActions : [],
    ],
    ["advantageEnhances", creative.advantageEnhances || {}],
    ["siteLinks", Array.isArray(creative.siteLinks) ? creative.siteLinks : []],
    ["messageTemplate", creative.messageTemplate || null],
    ["instantForm", creative.instantForm || null],
    ["utmParameters", creative.utmParameters || null],
    ["appConfiguration", creative.appConfiguration || null],
    ["instantExperience", creative.instantExperience || null],
    ["isInstantExperience", Boolean(creative.isInstantExperience)],
    ["productSetID", creative.productSetID || null],
    ["destinationFallBack", creative.destinationFallBack || null],
    ["address", creative.address || null],
    ["destinationType", creative.destinationTypeAd || ""],
  ];

  updates.forEach(([field, value]) => {
    if (field === "destinationType" && !value) {
      return;
    }
    setValue(`${basicPath}.${field}`, value);
  });

  return updates.map(([field]) => `${basicPath}.${field}`);
};

export const getMetaCreativeMediaHashInput = (
  mediaUrls = [],
  isCarousel = false
) => {
  const urls = (mediaUrls || []).filter(Boolean);
  if (!urls.length) {
    return "";
  }
  if (isCarousel || urls.length > 1) {
    return urls[1] ? `${urls[0]}${urls[1]}` : urls[0];
  }
  return urls[0];
};

export const buildMetaCreativeHashUrl = (
  mediaUrls = [],
  isCarousel = false
) => {
  const input = getMetaCreativeMediaHashInput(mediaUrls, isCarousel);
  if (!input) {
    return "";
  }
  return hashStringToID(input);
};

export const buildMetaCreativeCreationTimeHashUrl = ({
  hashUrl,
  creationTime,
} = {}) => {
  if (!hashUrl || !creationTime) {
    return "";
  }
  const inverted = String(CREATION_TIME_PAD_INVERT - creationTime).padStart(
    13,
    "0"
  );
  return `${hashUrl}_${inverted}`;
};

export const getMetaCreativeReuseCompatibility = ({
  libraryEntry,
  objective,
  destinationType,
  destinationTypeAd,
  optimizationGoal,
} = {}) => {
  if (!libraryEntry?.creativeID) {
    return { isValid: false, optimizationGoalMismatch: false };
  }

  const hardMatch =
    libraryEntry.objective === objective &&
    libraryEntry.destinationType === destinationType &&
    (!destinationTypeAd ||
      !libraryEntry.destinationTypeAd ||
      libraryEntry.destinationTypeAd === destinationTypeAd);

  const optimizationGoalMismatch =
    Boolean(optimizationGoal) &&
    Boolean(libraryEntry.optimizationGoal) &&
    libraryEntry.optimizationGoal !== optimizationGoal;

  return {
    isValid: hardMatch,
    optimizationGoalMismatch: hardMatch && optimizationGoalMismatch,
  };
};

export const inferMetaCreativeContentTypeFromUrls = ({
  mediaUrls = [],
  isCarousel = false,
} = {}) => {
  if (isCarousel || (mediaUrls || []).filter(Boolean).length > 1) {
    return "carousel";
  }
  const url = (mediaUrls?.[0] || "").toLowerCase().split("?")[0];
  if (VIDEO_FORMATS.some((ext) => url.endsWith(`.${ext}`))) {
    return "video";
  }
  if (IMAGE_FORMATS.some((ext) => url.endsWith(`.${ext}`))) {
    return "image";
  }
  return "image";
};

export const getMetaCreativeLibraryPath = ({
  advertiserID,
  adAccountID,
  pageID,
}) => `creativesByAdAccountAndPage/${advertiserID}/${adAccountID}/${pageID}`;

export const META_CREATIVE_ENGAGEMENT_TTL_MS = 60 * 60 * 1000;

export const isMetaCreativeEngagementCacheFresh = (
  syncedTime,
  now = Date.now()
) => {
  if (!syncedTime) {
    return false;
  }
  const parsed = Number(syncedTime);
  if (!Number.isFinite(parsed)) {
    return false;
  }
  return now - parsed < META_CREATIVE_ENGAGEMENT_TTL_MS;
};

/**
 * When adAccountID/pageID change in draft, reuse creativeIDs are no longer valid
 * for the new library scope. Exit reuse mode but keep the ad creative config data.
 */
export const clearMetaReusableCreativeModeFromAds = ({
  getValues,
  setValue,
  platform = "meta",
} = {}) => {
  if (!getValues || !setValue) return 0;

  const adsGroups = getValues("adsGroups") || [];
  let cleared = 0;

  adsGroups.forEach((adGroup, indexAdGroup) => {
    if (adGroup?.platform !== platform) return;

    (adGroup.ads || []).forEach((ad, indexAd) => {
      const isReuseMode =
        Boolean(ad?.reuseExistingCreative) ||
        (ad?.adSetupMode === "reuse" && Boolean(ad?.creativeID));
      if (!isReuseMode) return;

      const path = `adsGroups.[${indexAdGroup}].ads.[${indexAd}]`;
      setValue(`${path}.reuseExistingCreative`, false);
      setValue(`${path}.creativeID`, "");
      setValue(`${path}.adSetupMode`, "create");
      cleared += 1;
    });
  });

  return cleared;
};
